// src/pages/HistoryPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { ordersAPI } from '../services/api';
import { StatusBadge, LoadingOverlay, EmptyState, SearchInput, SelectFilter, toast } from '../components/UI';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'Delivered', label: 'Delivered' },
  { value: 'Failed', label: 'Failed' },
  { value: 'In Transit', label: 'In Transit' },
  { value: 'Picked Up', label: 'Picked Up' },
  { value: 'Assigned', label: 'Assigned' },
  { value: 'Pending', label: 'Pending' },
];

const DATE_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'This Week' },
  { value: 'month', label: 'This Month' },
];

export default function HistoryPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [dateRange, setDateRange] = useState('');

  useEffect(() => {
    ordersAPI.getAll()
      .then(data => setOrders(data || []))
      .catch(() => toast.error('Failed to load history.'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = orders.filter(o => {
    const q = search.toLowerCase();
    const matchQ = !q || [o.package_id, o.receiver_name, o.address, o.sender_name, String(o.id)]
      .some(f => f?.toLowerCase().includes(q));
    const matchStatus = !status || o.status === status;

    let matchDate = true;
    if (dateRange === 'today') matchDate = moment(o.created_at).isSame(moment(), 'day');
    else if (dateRange === 'week') matchDate = moment(o.created_at).isSame(moment(), 'week');
    else if (dateRange === 'month') matchDate = moment(o.created_at).isSame(moment(), 'month');

    return matchQ && matchStatus && matchDate;
  }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const delivered = filtered.filter(o => o.status === 'Delivered').length;
  const failed = filtered.filter(o => o.status === 'Failed').length;

  if (loading) return <LoadingOverlay message="Loading history…" />;

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <h1 className="page-title">Order History</h1>
        <p className="page-sub">Full record of all orders across all time</p>
      </div>

      {/* ─── Summary ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Showing', value: filtered.length, color: 'var(--gray-900)' },
          { label: 'Delivered', value: delivered, color: 'var(--success)' },
          { label: 'Failed', value: failed, color: 'var(--danger)' },
          { label: 'Total in DB', value: orders.length, color: 'var(--gray-400)' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--white)', borderRadius: 10, padding: '10px 18px',
            border: '1px solid var(--gray-200)',
            display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 12, color: 'var(--gray-400)', fontWeight: 600 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ─── Filters ──────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <SearchInput value={search} onChange={setSearch} placeholder="Search ID, package, receiver…" />
        <SelectFilter value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        <SelectFilter value={dateRange} onChange={setDateRange} options={DATE_OPTIONS} />
        {(search || status || dateRange) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setSearch(''); setStatus(''); setDateRange(''); }}>
            ✕ Clear filters
          </button>
        )}
      </div>

      {/* ─── Table ────────────────────────────────────────────────────── */}
      <div className="card">
        {filtered.length === 0 ? (
          <EmptyState icon="" title="No orders match" subtitle="Try adjusting your filters." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>#ID</th>
                  <th>Package ID</th>
                  <th>Sender</th>
                  <th>Receiver</th>
                  <th>Phone</th>
                  <th>Address</th>
                  <th>Status</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(o => (
                  <tr
                    key={o.id}
                    onClick={() => navigate(`/orders/${o.id}`)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="td-mono">#{o.id}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--primary)' }}>
                      {o.package_id}
                    </td>
                    <td style={{ fontSize: 13 }}>{o.sender_name}</td>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{o.receiver_name}</td>
                    <td className="td-mono" style={{ fontSize: 12 }}>{o.phone}</td>
                    <td style={{ maxWidth: 160, fontSize: 12 }}>
                      <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.address}
                      </div>
                    </td>
                    <td><StatusBadge status={o.status} /></td>
                    <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>
                      {moment(o.created_at).format('MMM D, YYYY')}
                      <br />
                      <span style={{ opacity: 0.7 }}>{moment(o.created_at).format('h:mm A')}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}